import { Button } from '@/components/ui/button';
import { AlertTriangle, Globe, UserX } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { PlayersController } from '@/pages/players/usePlayersController';
import { playerDisplayName } from './player-utils';
import { PlayerStatusBadge } from './PlayerStatusBadge';
import { StatusToggle } from './StatusToggle';

interface PlayerCardListProps {
  ctrl: PlayersController;
}

export function PlayerCardList({ ctrl }: PlayerCardListProps) {
  const { t } = useTranslation('director');
  const {
    players,
    canEdit,
    canWithdrawPlayers,
    isPlayerDuplicate,
    setSelectedPlayer,
    openProfileViewer,
    handleStatusChange,
    handleWithdraw,
    handleUnblockPlayer,
  } = ctrl;

  return (
    <div className="md:hidden" data-testid="player-cards">
      {players.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">{t('players_page.empty_cards')}</p>
      ) : (
        <div className="space-y-2">
          {players.map((player) => {
            const displayName = playerDisplayName(player);
            const isDupe = isPlayerDuplicate(player);
            const isWithdrawn =
              player.status === 'withdrawn' || player.status === 'eliminated';
            return (
              <div
                key={player.id}
                data-testid={`player-card-${displayName}`}
                className={`rounded-lg border p-3 ${
                  isDupe ? 'border-yellow-400 bg-yellow-50' : 'bg-card'
                } ${isWithdrawn ? 'opacity-50' : ''}`}
                onClick={() => setSelectedPlayer(player)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    {isDupe && (
                      <span data-testid={`duplicate-flag-${displayName}`}>
                        <AlertTriangle className="h-4 w-4 shrink-0 text-yellow-600" />
                      </span>
                    )}
                    {player.profileUserId && (
                      <Button
                        variant="outline"
                        size="sm"
                        title={t('players_page.self_enrolled_title')}
                        aria-label={t('players_page.self_enrolled_aria', { name: displayName })}
                        data-testid={`self-enrolled-indicator-${displayName}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          openProfileViewer(player);
                        }}
                        className="h-7 w-7 shrink-0 p-0"
                      >
                        <Globe className="h-4 w-4" />
                      </Button>
                    )}
                    <p
                      className="font-medium truncate min-w-0"
                      data-testid={`card-name-${displayName}`}
                    >
                      {displayName}
                    </p>
                  </div>
                  <div
                    className="shrink-0 ml-2 flex items-center gap-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {canWithdrawPlayers && player.status === 'accepted' && (
                      <Button
                        variant="destructive"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        data-testid={`withdraw-player-${displayName}`}
                        onClick={() => handleWithdraw(player)}
                      >
                        <UserX className="mr-1 h-3.5 w-3.5" />
                        {t('players_page.withdraw_button')}
                      </Button>
                    )}
                    {canEdit && !isWithdrawn && player.status !== 'blocked' ? (
                      <StatusToggle
                        status={player.status}
                        onChange={(s) => handleStatusChange(player, s)}
                        testIdPrefix={`player-${displayName}`}
                      />
                    ) : (
                      <>
                        <span data-testid={`player-${displayName}-status-badge`}>
                          <PlayerStatusBadge status={player.status} />
                        </span>
                        {canEdit && player.status === 'blocked' && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            data-testid={`player-${displayName}-unblock`}
                            onClick={() => handleUnblockPlayer(player)}
                          >
                            {t('players_page.unblock_button')}
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
